import React, { useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';
import { TextField, Typography } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import StarEdgyFilledIcon from '../../../../assets/icons/StarEdgyFilledIcon';
import StarEdgyIcon from '../../../../assets/icons/StarEdgyIcon';
import { CustomButton } from '../../../../components/UI/CustomButton';
import { useAlert } from '../../../../context/AlertProvider';
import { CREATE_REVIEW } from '../../../../graphql/videos';
import { colorsPalette, MIDDLE_MARGIN, SMALL_MARGIN } from '../../../../theme';
import { Mutation, ReviewableType, ReviewCreateInput } from '../../../../types';
import useValidation from '../../../../utils/useValidation';
import useStyles from './styles';

const stars = new Array(5).fill(null);

const ReviewForm = ({ id, type = ReviewableType.Video }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [selectedStar, setSelectedStar] = useState(-1);
  const [showForm, setShowForm] = useState(false);
  const { showAlert } = useAlert();

  const [createReview] = useMutation<Pick<Mutation, 'createReview'>, { input: ReviewCreateInput }>(
    CREATE_REVIEW,
  );

  useEffect(() => {
    if (selectedStar > -1) {
      setShowForm(true);
    } else {
      setShowForm(false);
    }
  }, [selectedStar]);

  const onSubmit = async ({ review }) => {
    await createReview({
      variables: {
        input: {
          comment: review ? review.trim() : null,
          stars_rating: selectedStar + 1,
          reviewable_type: type,
          reviewable_id: id,
        },
      },
    });

    resetAll();
    showAlert({ message: t('page.video.reviewSubmitted') });
  };

  const { handleChange, handleSubmit, resetToDefault, values, errors } = useValidation(
    {
      review: '',
    },
    null,
    onSubmit,
  );

  const resetAll = () => {
    resetToDefault();
    setSelectedStar(-1);
  };

  const handleSelect = (i) => {
    setSelectedStar(i === selectedStar ? -1 : i);
  };

  return (
    <Wrapper>
      <Title variant="h4">{t('page.video.letUsKnow')}</Title>
      <StarsBlock>
        <div>
          <Stars>
            {stars.map((s, i) =>
              i <= selectedStar ? (
                <StarFilled
                  data-testid="reviewFormFilledStart"
                  key={i}
                  onClick={() => handleSelect(i)}
                />
              ) : (
                <StarOutline
                  data-testid="reviewFormOutlinedStart"
                  key={i}
                  onClick={() => handleSelect(i)}
                />
              ),
            )}
          </Stars>
          <Captions>
            <Caption>{t('page.video.dissatisfied')}</Caption>
            <Caption>{t('page.video.highlySatisfied')}</Caption>
          </Captions>
        </div>
      </StarsBlock>
      {showForm && (
        <>
          <TextField
            className={classes.field}
            name="review"
            label={t('page.video.yourReview')}
            variant="outlined"
            value={values.review}
            onChange={handleChange}
            error={!!errors?.review}
            helperText={errors?.review}
            multiline
            rows={3}
            inputProps={{ 'data-testid': 'reviewField' }}
          />
          <Buttons>
            <CustomButton data-testid="cancelReview" color="secondary" onClick={resetAll}>
              {t('button.cancel')}
            </CustomButton>
            <CustomButton
              data-testid="submitReview"
              btnClass={classes.submitButton}
              onClick={handleSubmit}
            >
              {t('page.video.leaveReview')}
            </CustomButton>
          </Buttons>
        </>
      )}
    </Wrapper>
  );
};

export default ReviewForm;

const Buttons = styled('div')(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  flexDirection: 'column-reverse',
  justifyContent: 'flex-end',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));
const StarsBlock = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: SMALL_MARGIN,
});
const Captions = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
});
const Caption = styled('span')({
  color: colorsPalette.blue,
});
const Stars = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: 8,
});
const StarOutline = styled(StarEdgyIcon)(({ theme }: { theme: Theme }) => ({
  width: 36,
  height: 36,
  cursor: 'pointer',
  '&:not(:last-child)': {
    marginRight: SMALL_MARGIN,
    [theme.breakpoints.up('md')]: {
      marginRight: 24,
    },
  },
  [theme.breakpoints.up('md')]: {
    width: 56,
    height: 56,
  },
}));
const StarFilled = styled(StarEdgyFilledIcon)(({ theme }: { theme: Theme }) => ({
  width: 36,
  height: 36,
  cursor: 'pointer',
  '&:not(:last-child)': {
    marginRight: SMALL_MARGIN,
    [theme.breakpoints.up('md')]: {
      marginRight: 24,
    },
  },
  [theme.breakpoints.up('md')]: {
    width: 56,
    height: 56,
  },
}));
const Title = styled(Typography)({
  marginBottom: SMALL_MARGIN,
  fontSize: 18,
  textAlign: 'center',
});
const Wrapper = styled('div')(({ theme }: { theme: Theme }) => ({
  width: '100%',
  padding: `${MIDDLE_MARGIN}px 24px`,
  marginBottom: MIDDLE_MARGIN,
  borderRadius: 8,
  background: colorsPalette.blackSecondary,
  [theme.breakpoints.up('md')]: {
    width: 'initial',
  },
}));
